import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Package,
  Eye,
  ToggleLeft,
  ToggleRight
} from 'lucide-react'
// import { productAPI } from '../services/api' // Removed API calls
import { LoadingSpinner } from '../components'
import { Button } from '../components'
import toast from 'react-hot-toast'

function Products() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)

  const queryClient = useQueryClient()

  // Fetch products
  const { data: productsData, isLoading } = useQuery(
    ['products', { page: currentPage, search: searchTerm, category: selectedCategory, isActive: selectedStatus }],
    () => productAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      category: selectedCategory,
      isActive: selectedStatus,
    })
  )

  // Create product mutation
  const createProductMutation = useMutation(productAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('products')
      setShowModal(false)
      toast.success('Product created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create product')
    }
  })

  // Update product mutation
  const updateProductMutation = useMutation(
    ({ id, data }) => productAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products')
        setShowModal(false)
        setEditingProduct(null)
        toast.success('Product updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update product')
      }
    }
  )

  // Delete product mutation
  const deleteProductMutation = useMutation(productAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('products')
      toast.success('Product deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete product')
    }
  })

  // Toggle status mutation
  const toggleStatusMutation = useMutation(productAPI.toggleStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries('products')
      toast.success('Product status updated successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update product status')
    }
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingProduct(null)
    reset()
  }

  const handleEdit = (product) => {
    setEditingProduct(product)
    setShowModal(true)
    reset(product)
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteProductMutation.mutate(id)
    }
  }

  const handleToggleStatus = (id) => {
    toggleStatusMutation.mutate(id)
  }

  const onSubmit = (data) => {
    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct.id, data })
    } else {
      createProductMutation.mutate(data)
    }
  }

  const products = productsData?.data?.products || []

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Products</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage dress products and their details
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          variant="primary"
          size="md"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="label">Category</label>
              <select
                className="input"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="Formal">Formal</option>
                <option value="Casual">Casual</option>
                <option value="Traditional">Traditional</option>
                <option value="Wedding">Wedding</option>
                <option value="Party">Party</option>
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select
                className="input"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('')
                  setSelectedStatus('')
                }}
                className="w-full"
                variant="outline"
                size="md"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card">
        <div className="card-content">
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-cell">Dress Name</th>
                  <th className="table-cell">Category</th>
                  <th className="table-cell">Tailor</th>
                  <th className="table-cell">Total Amount</th>
                  <th className="table-cell">Status</th>
                  <th className="table-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-secondary-200">
                    <td className="table-cell">
                      <div>
                        <div className="font-medium text-secondary-900">{product.dressName}</div>
                        {product.dressCode && (
                          <div className="text-sm text-secondary-500">Code: {product.dressCode}</div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        {product.category || 'N/A'}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div>
                        <div className="text-sm text-secondary-900">{product.tailorName || 'N/A'}</div>
                        {product.workTailorName && (
                          <div className="text-xs text-secondary-500">Work: {product.workTailorName}</div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-secondary-900">
                        ₹{product.totalAmount || 0}
                      </div>
                      <div className="text-xs text-secondary-500">
                        Stitching: ₹{product.stitchingAmount || 0}
                      </div>
                    </td>
                    <td className="table-cell">
                      <button
                        onClick={() => handleToggleStatus(product.id)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {product.isActive ? (
                          <>
                            <ToggleRight className="h-3 w-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="h-3 w-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {productsData?.data?.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-secondary-700">
                Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, productsData.data.totalItems)} of {productsData.data.totalItems} products
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, productsData.data.totalPages))}
                  disabled={currentPage === productsData.data.totalPages}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-2xl">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Dress Name *</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Enter dress name"
                      {...register('dressName', { required: 'Dress name is required' })}
                    />
                    {errors.dressName && (
                      <p className="form-error">{errors.dressName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Category</label>
                    <select className="input" {...register('category')}>
                      <option value="">Select Category</option>
                      <option value="Formal">Formal</option>
                      <option value="Casual">Casual</option>
                      <option value="Traditional">Traditional</option>
                      <option value="Wedding">Wedding</option>
                      <option value="Party">Party</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Tailor Name</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Enter tailor name"
                      {...register('tailorName')}
                    />
                  </div>
                  <div>
                    <label className="label">Work Tailor Name</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Enter work tailor name"
                      {...register('workTailorName')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Stitching Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="0.00"
                      {...register('stitchingAmount')}
                    />
                  </div>
                  <div>
                    <label className="label">Work Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="0.00"
                      {...register('workAmount')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Cloth Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="0.00"
                      {...register('clothAmount')}
                    />
                  </div>
                  <div>
                    <label className="label">Total Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="0.00"
                      {...register('totalAmount')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Cloth Name</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Enter cloth name"
                      {...register('clothName')}
                    />
                  </div>
                  <div>
                    <label className="label">Dress Code</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Enter dress code"
                      {...register('dressCode')}
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Extra Notes</label>
                  <textarea
                    className="input"
                    rows={3}
                    placeholder="Enter any additional notes"
                    {...register('extraNotes')}
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    onClick={handleCloseModal}
                    variant="outline"
                    size="md"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createProductMutation.isLoading || updateProductMutation.isLoading}
                    variant="primary"
                    size="md"
                    loading={createProductMutation.isLoading || updateProductMutation.isLoading}
                  >
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Products
